import pb from '../lib/pocketbase';
import { AdvertisementPriority } from '../types';

export type BillingReportPreset = 'last_7_days' | 'last_30_days' | 'this_month' | 'previous_month' | 'custom';

export type BillingReportMetricMode = 'ads' | 'quotas';

export interface BillingReportFilters {
  startDate: Date;
  endDate: Date;
  partnerId?: string; // undefined = all partners
  metricMode?: BillingReportMetricMode;
}

export interface BillingReportTotals {
  adCounts: Record<AdvertisementPriority, number>;
  magicLinkClicks: number;
  promoRequests: number;
}

export interface BillingReportPartnerRow extends BillingReportTotals {
  partnerId: string;
}

export interface BillingReportResult {
  totals: BillingReportTotals;
  perPartner: BillingReportPartnerRow[];
}

const PRIORITIES: AdvertisementPriority[] = ['platinum', 'gold', 'silver', 'bronze'];

const createEmptyAdCounts = (): Record<AdvertisementPriority, number> => ({
  platinum: 0,
  gold: 0,
  silver: 0,
  bronze: 0
});

const normalizePriority = (value: any): AdvertisementPriority => {
  if (PRIORITIES.includes(value)) {
    return value as AdvertisementPriority;
  }
  const fallbackMap: Record<string, AdvertisementPriority> = {
    '3': 'platinum',
    '2': 'gold',
    '1': 'silver',
    '0': 'bronze'
  };
  if (value in fallbackMap) {
    return fallbackMap[value];
  }
  return 'bronze';
};

const buildDateFilter = (field: string, startISO: string, endISO: string) =>
  `${field} >= "${startISO}" && ${field} <= "${endISO}"`;

const buildPartnerFilter = (partnerId?: string) => (partnerId ? `partner_id = "${partnerId}"` : '');

const combineFilters = (...filters: string[]) => filters.filter(Boolean).join(' && ');

export async function getBillingReport(filters: BillingReportFilters): Promise<BillingReportResult> {
  const startISO = filters.startDate.toISOString();
  const endISO = filters.endDate.toISOString();
  const partnerFilter = buildPartnerFilter(filters.partnerId);
  const metricMode: BillingReportMetricMode = filters.metricMode || 'ads';

  const totals: BillingReportTotals = {
    adCounts: createEmptyAdCounts(),
    magicLinkClicks: 0,
    promoRequests: 0
  };

  const perPartnerMap = new Map<string, BillingReportPartnerRow>();

  const ensurePartnerRow = (partnerId: string) => {
    if (!perPartnerMap.has(partnerId)) {
      perPartnerMap.set(partnerId, {
        partnerId,
        adCounts: createEmptyAdCounts(),
        magicLinkClicks: 0,
        promoRequests: 0
      });
    }
    return perPartnerMap.get(partnerId)!;
  };

  if (metricMode === 'quotas') {
    // Use allocated priority quotas instead of created advertisements
    try {
      const quotaFilter = partnerFilter ? partnerFilter.replace('partner_id', 'id') : '';
      const partners = await pb.collection('partners').getFullList({
        filter: quotaFilter,
        $autoCancel: false,
        fields: 'id,priority_quotas'
      });

      partners.forEach((partner: any) => {
        const quotas = partner.priority_quotas || {};
        const row = ensurePartnerRow(partner.id);

        PRIORITIES.forEach(priority => {
          const allocation = Number(quotas[priority] ?? 0);
          totals.adCounts[priority] += allocation;
          row.adCounts[priority] += allocation;
        });
      });
    } catch (error) {
      console.error('BillingReport: Failed to fetch partner quotas', error);
    }
  } else {
    // Default: count advertisements created in range
    try {
      const adFilter = combineFilters(
        buildDateFilter('created', startISO, endISO),
        partnerFilter
      );

      const ads = await pb.collection('advertisements').getFullList({
        filter: adFilter,
        $autoCancel: false,
        fields: 'id,partner_id,priority,created'
      });

      ads.forEach((ad: any) => {
        const priority = normalizePriority(ad.priority);
        totals.adCounts[priority] += 1;

        const partnerId = ad.partner_id;
        if (partnerId) {
          const row = ensurePartnerRow(partnerId);
          row.adCounts[priority] += 1;
        }
      });
    } catch (error) {
      console.error('BillingReport: Failed to fetch advertisements', error);
    }
  }

  // Fetch click tracking events (magic link clicks + promo requests)
  try {
    const clickFilter = combineFilters(
      buildDateFilter('created', startISO, endISO),
      '(click_type = "public_link_click" || click_type = "promo_request")',
      partnerFilter
    );

    const clickRecords = await pb.collection('click_tracking').getFullList({
      filter: clickFilter,
      $autoCancel: false,
      fields: 'id,partner_id,click_type,created'
    });

    clickRecords.forEach((record: any) => {
      const partnerId = record.partner_id;
      if (!partnerId) return;

      const row = ensurePartnerRow(partnerId);

      if (record.click_type === 'public_link_click') {
        totals.magicLinkClicks += 1;
        row.magicLinkClicks += 1;
      }

      if (record.click_type === 'promo_request') {
        totals.promoRequests += 1;
        row.promoRequests += 1;
      }
    });
  } catch (error) {
    console.error('BillingReport: Failed to fetch click tracking records', error);
  }

  const perPartner = Array.from(perPartnerMap.values()).sort((a, b) => a.partnerId.localeCompare(b.partnerId));

  return {
    totals,
    perPartner
  };
}
